import { useCallback, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useDeleteObjective } from "@/hooks/useObjectives";
import { useDeleteKeyResult } from "@/hooks/useKeyResults";
import { okrErrorDescription } from "@/lib/okr-errors";
import { KeyResult, Objective } from "@/types";

/** Sheet + objective/KR dialog state and their mutation handlers for /okrs. */
export function useOkrDialogs() {
  const { toast } = useToast();

  const [selectedObjective, setSelectedObjective] = useState<Objective | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const [isObjDialogOpen, setIsObjDialogOpen] = useState(false);
  const [editingObjective, setEditingObjective] = useState<Objective | null>(null);
  const [isObjDeleteOpen, setIsObjDeleteOpen] = useState(false);
  const [objectiveToDelete, setObjectiveToDelete] = useState<Objective | null>(null);

  const [isKrDialogOpen, setIsKrDialogOpen] = useState(false);
  const [editingKeyResult, setEditingKeyResult] = useState<KeyResult | null>(null);
  const [parentObjectivePid, setParentObjectivePid] = useState<string | undefined>();
  const [isKrDeleteOpen, setIsKrDeleteOpen] = useState(false);
  const [keyResultToDelete, setKeyResultToDelete] = useState<KeyResult | null>(null);

  const deleteObjMutation = useDeleteObjective();
  const deleteKrMutation = useDeleteKeyResult();

  const openCreateObjective = useCallback(() => { setEditingObjective(null); setIsObjDialogOpen(true); }, []);
  const viewObjective = useCallback((obj: Objective) => { setSelectedObjective(obj); setIsSheetOpen(true); }, []);
  const editObjective = useCallback((obj: Objective) => { setEditingObjective(obj); setIsObjDialogOpen(true); }, []);
  const deleteObjective = useCallback((obj: Objective) => { setObjectiveToDelete(obj); setIsObjDeleteOpen(true); }, []);

  const confirmDeleteObjective = useCallback(() => {
    if (!objectiveToDelete) return;
    deleteObjMutation.mutate(objectiveToDelete.pid, {
      onSuccess: () => {
        toast({ title: "Objective deleted", description: "The objective has been permanently deleted." });
        setIsObjDeleteOpen(false);
        setObjectiveToDelete(null);
      },
      onError: (error) =>
        toast({ title: "Error", description: okrErrorDescription(error, "Failed to delete objective."), variant: "destructive" }),
    });
  }, [objectiveToDelete, deleteObjMutation, toast]);

  const addKeyResult = useCallback((obj: Objective) => {
    setEditingKeyResult(null); setParentObjectivePid(obj.pid); setIsKrDialogOpen(true);
  }, []);
  const editKeyResult = useCallback((kr: KeyResult) => {
    setEditingKeyResult(kr); setParentObjectivePid(undefined); setIsKrDialogOpen(true);
  }, []);
  const deleteKeyResult = useCallback((kr: KeyResult) => { setKeyResultToDelete(kr); setIsKrDeleteOpen(true); }, []);

  const confirmDeleteKeyResult = useCallback(() => {
    if (!keyResultToDelete) return;
    deleteKrMutation.mutate(keyResultToDelete.pid, {
      onSuccess: () => {
        toast({ title: "Key result deleted", description: "The key result has been permanently deleted." });
        setIsKrDeleteOpen(false);
        setKeyResultToDelete(null);
      },
      onError: (error) =>
        toast({ title: "Error", description: okrErrorDescription(error, "Failed to delete key result."), variant: "destructive" }),
    });
  }, [keyResultToDelete, deleteKrMutation, toast]);

  // Keep the open sheet/dialog showing the saved version of the objective.
  const objectiveSaved = useCallback((objective: Objective) => {
    setSelectedObjective((prev) => (prev?.pid === objective.pid ? objective : prev));
    setEditingObjective((prev) => (prev?.pid === objective.pid ? objective : prev));
  }, []);

  return {
    openCreateObjective,
    list: {
      onViewObjective: viewObjective,
      onEditObjective: editObjective,
      onDeleteObjective: deleteObjective,
      onAddKeyResult: addKeyResult,
      onEditKeyResult: editKeyResult,
      onDeleteKeyResult: deleteKeyResult,
    },
    sheet: {
      objective: selectedObjective,
      open: isSheetOpen,
      onOpenChange: setIsSheetOpen,
      onEdit: editObjective,
      onDelete: deleteObjective,
      onAddKeyResult: addKeyResult,
      onEditKeyResult: editKeyResult,
      onDeleteKeyResult: deleteKeyResult,
    },
    objectiveDialogs: {
      isFormOpen: isObjDialogOpen,
      onFormOpenChange: setIsObjDialogOpen,
      onObjectiveSaved: objectiveSaved,
      editingObjective,
      isDeleteOpen: isObjDeleteOpen,
      onDeleteOpenChange: setIsObjDeleteOpen,
      objectiveToDelete,
      onConfirmDelete: confirmDeleteObjective,
      isDeleting: deleteObjMutation.isPending,
    },
    keyResultDialogs: {
      isFormOpen: isKrDialogOpen,
      onFormOpenChange: setIsKrDialogOpen,
      editingKeyResult,
      parentObjectivePid,
      isDeleteOpen: isKrDeleteOpen,
      onDeleteOpenChange: setIsKrDeleteOpen,
      keyResultToDelete,
      onConfirmDelete: confirmDeleteKeyResult,
      isDeleting: deleteKrMutation.isPending,
    },
  };
}
